import React, { useEffect, useState } from 'react';

import { Card } from '@material-ui/core';
import { useFormik } from 'formik';
import _ from 'lodash';
import { Button, Col, Form, Row } from 'react-bootstrap';
import { injectIntl } from 'react-intl';
import { shallowEqual, useSelector } from 'react-redux';
import * as Yup from 'yup';

import { useSubheader } from '../../../_metronic/layout';
import { submissionCardsFields, submissionFields } from '../../../airtable/metadata/submissions';
import { fetchCoupons, fetchRates, fetchShipping, fetchTexts } from '../../../airtable/settings';
import { createSubmission } from '../../../airtable/submissions';
import { computeSubmissionCart, createSubmissionCart } from '../../../business/submission-cart';
import { formatCurrency } from '../../../commons/utils';
import { createCheckoutSession } from '../../../stripe';
import { DynamicTable } from '../../components/dynamic-table';
import { LoadingOverlay } from '../../components/loading-overlay';

import { createColumns } from './commons/columns';
import { clearDraft, restoreDraftForm, restoreDraftTable, storeDraftForm, storeDraftTable } from './commons/memory';
import { useStyles } from './commons/styles';

const initialValues = {
  cards: 0,
  couponCode: '',
  date: '',
  noReturnShipping: false,
  tracking: '',
};

const columns = createColumns();

function DraftSubmissionPage(props) {
  const { intl } = props;

  const classes = useStyles();
  const user = useSelector(({ auth }) => auth.user, shallowEqual);
  const subheader = useSubheader();
  subheader.setTitle('Draft');

  const [computedCart, setComputedCart] = useState(null);
  const [coupons, setCoupons] = useState(null);
  const [rates, setRates] = useState(null);
  const [shipping, setShipping] = useState(null);
  const [refresh, setRefresh] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [rows, setRows] = useState(restoreDraftTable());
  const [textBelowSubmit, setTextBelowSubmit] = useState(null);

  useEffect(() => {
    async function fetchAll() {
      const texts = await fetchTexts();
      setTextBelowSubmit(texts.get('submissions-below-submit'));
    }
    void fetchAll();
  }, []);

  useEffect(() => {
    async function fetchAll() {
      setRefreshing(true);

      try {
        fetchCoupons().then(setCoupons);
        fetchRates().then(setRates);
        fetchShipping().then(setShipping);
      } finally {
        setRefreshing(false);
      }
    }

    if (refresh) {
      setRefresh(false);
      void fetchAll();
    }
  }, [refresh, user]);

  const schema = Yup.object().shape({
    cards: Yup.number()
      .min(1)
      .required(intl.formatMessage({ id: 'AUTH.VALIDATION.REQUIRED_FIELD' })),

    date: Yup.date()
      .required(intl.formatMessage({ id: 'AUTH.VALIDATION.REQUIRED_FIELD' })),

    couponCode: Yup.string(),
    noReturnShipping: Yup.boolean(),
    tracking: Yup.string(),
  });

  const onSubmit = async (values, { resetForm, setStatus, setSubmitting }) => {
    for (const row of rows) {
      for (const column of columns) {
        const decision = column.validate(row);

        if (decision === true) {
          continue;
        }

        setStatus({ error: true, text: 'One or more cards have are invalid: ' + decision.helperText });
        return;
      }
    }

    const submissionCards = [];

    for (const row of rows) {
      const submittingRow = { ...row };

      // if (submittingRow.dciReview === true) {
      submittingRow[submissionCardsFields.DESTINATION] = 'DCI';
      delete submittingRow.dciReview;
      // }

      const copies = Math.max(1, submittingRow.quantity);
      submissionCards.push(...Array(copies).fill(submittingRow));
    }

    console.log('submitting', { rows: submissionCards, values });
    setStatus();
    setSubmitting(true);

    try {
      const submission = formikValuesToSubmissionFields(values);
      const response = await createSubmission(submission, submissionCards, user);

      clearDraft();
      onRowsChanged([]);
      resetForm();
      setSubmitting(true); // again, because resetForm sets false
      setStatus({
        error: false,
        text: 'Cards submitted successfully.',
      });

      if ((computedCart?.total ?? 0) > 0 && await checkoutWithStripe(response.submission)) {
        return;
      }

      setTimeout(() => setStatus(), 5000);

    } catch (error) {
      const text = error.response?.data?.raw
        ? `${error.message}.\nStripe returned this message: ${error.response.data.raw.message}\nStripe request ID: ${error.response.data.raw.requestId}`
        : error.response?.data?.message
          ? `${error.message}: ${error.response.data.message}`
          : error.message;

      setStatus({ error: true, text });
    } finally {
      setSubmitting(false);
    }
  };

  async function checkoutWithStripe(submission) {
    const session = await createCheckoutSession({
      target: submission.id,
      type: 'submission',
      user,
    });

    if (session.url) {
      window.location.href = session.url;
      return true;
    }

    return false;
  }

  const formik = useFormik({
    initialValues: {
      ...initialValues,
      ...submissionFieldsToFormikValues(restoreDraftForm()),
    },
    onSubmit,
    validateOnMount: true,
    validationSchema: schema,
  });

  useEffect(() => {
    if (!coupons) {
      return;
    }

    const submission = formikValuesToSubmissionFields(formik.values);
    storeDraftForm(submission);

    if (coupons && rates && shipping) {
      const cart = createSubmissionCart(submission, rows);
      const computedCart = computeSubmissionCart(cart, { coupons, rates, shipping });

      setComputedCart(computedCart);
    } else {
      setComputedCart(null);
    }
  }, [formik.values, coupons, rates, shipping, rows]);

  const onRowsChanged = rows => {
    setRows(rows);
    formik.setFieldValue('cards', _.sumBy(rows, 'quantity'));
    storeDraftTable(rows);
  };

  function isLoading() {
    return refreshing || formik.isSubmitting;
  }

  function renderSubmissionCartTotals() {
    const discount = computedCart?.discountsTotal ?? 0;
    const insurance = computedCart?.insuranceTotal ?? 0;
    const rates = computedCart?.ratesTotal ?? 0;
    const shipping = computedCart?.shippingTotal ?? 0;
    const total = computedCart?.total ?? 0;

    const services = rates + discount;

    return (
      <>
        {renderSubmissionCartTotal('Services', services)}
        {renderSubmissionCartTotal('Shipping', shipping)}
        {renderSubmissionCartTotal('Insurance', insurance)}
        {renderSubmissionCartTotal('Discount', discount)}
        {renderSubmissionCartTotal('Total', total, 'mt-2 font-size-h4')}
      </>
    );

    function renderSubmissionCartTotal(title, value, additionalClasses) {
      return (
        <Row className={`${classes.totalContainer} ${additionalClasses}`}>
          <span>{title}:</span>
          <span className={classes.totalValue}>{formatCurrency(value)}</span>
        </Row>
      );
    }
  }

  function renderSubmissionCard() {
    return (
      <Card className={classes.card}>
        <LoadingOverlay active={isLoading()}>
          <DynamicTable
            columns={columns}
            editable={!isLoading()}
            editableOptions={{
              add: true,
              bulk: true,
              continuous: true,
              delete: true,
              duplicate: true,
              single: false,
            }}
            onRowsChanged={onRowsChanged}
            rows={rows}
            title="Submission"
          />

          <Form onSubmit={formik.handleSubmit} className={classes.form}>
            <Form.Row>
              <Form.Group as={Col} controlId="date">
                <Form.Label>Date sent</Form.Label>
                <Form.Control
                  disabled={isLoading()}
                  isInvalid={formik.errors.date}
                  name="date"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  type="date"
                  value={formik.values.date}
                />
                <Form.Control.Feedback type="invalid">{formik.errors.date}</Form.Control.Feedback>
              </Form.Group>

              <Form.Group as={Col} controlId="tracking">
                <Form.Label>Tracking number (if available)</Form.Label>
                <Form.Control
                  disabled={isLoading()}
                  isInvalid={formik.errors.tracking}
                  name="tracking"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  placeholder="Enter tracking number"
                  type="text"
                  value={formik.values.tracking}
                />
                <Form.Control.Feedback type="invalid">{formik.errors.tracking}</Form.Control.Feedback>
              </Form.Group>

              <Form.Group as={Col} controlId="cards">
                <Form.Label>Number of cards (above)</Form.Label>
                <Form.Control
                  disabled
                  isInvalid={formik.errors.cards}
                  name="cards"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  type="number"
                  value={formik.values.cards}
                />
                <Form.Control.Feedback type="invalid">{formik.errors.cards}</Form.Control.Feedback>
              </Form.Group>
            </Form.Row>

            <Form.Row className="d-flex align-items-end">
              <Col xs={5} className="d-flex">
                <Button
                  className={classes.submitButton}
                  disabled={isLoading() || !formik.isValid}
                  type="submit"
                  variant="primary"
                >
                  <span>{(computedCart?.total ?? 0) > 0 ? 'Checkout' : 'Submit'}</span>
                  {isLoading() && <span className="ml-3 spinner spinner-white" />}
                </Button>
              </Col>

              <Col xs={4}>
                <Row className="mb-2">
                  <Form.Check
                    disabled={isLoading()}
                    label="No return shipping - I will pick up from the shop"
                    name="noReturnShipping"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    type="checkbox"
                    value={formik.values.noReturnShipping}
                  />
                </Row>

                <Row>
                  <Col className="d-flex align-items-end pl-0 pr-0">
                    <Form.Control
                      disabled={isLoading()}
                      isInvalid={formik.errors.couponCode}
                      name="couponCode"
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      placeholder="Enter coupon code"
                      type="text"
                      value={formik.values.couponCode}
                    />
                  </Col>
                </Row>
              </Col>

              <Col xs={3} className="align-items-end">
                {renderSubmissionCartTotals()}
              </Col>

            </Form.Row>
          </Form>
        </LoadingOverlay>
      </Card>
    );
  }

  function renderTextBelowSubmitCard() {
    if (!textBelowSubmit) {
      return null;
    }

    return (
      <Card className={classes.card} style={{ padding: '1rem', whiteSpace: 'pre-line' }}>
        {textBelowSubmit}
      </Card>
    );
  }

  return (
    <div className={classes.root}>
      {formik.status && (
        <div className={`alert alert-custom alert-dismissible alert-light-${formik.status.error ? 'danger' : 'success'} ${classes.status}`}>
          <div className="alert-text font-weight-bold" style={{ whiteSpace: 'pre-line' }}>
            {formik.status.text}
          </div>
        </div>
      )}

      {renderSubmissionCard()}
      {renderTextBelowSubmitCard()}

    </div>
  );
}

function formikValuesToSubmissionFields(values) {
  const { cards, couponCode, date, noReturnShipping, tracking } = values;

  return {
    [submissionFields.CARDS_SENT]: cards,
    [submissionFields.COUPON_CODE]: couponCode,
    [submissionFields.DATE_SENT]: date,
    [submissionFields.NO_RETURN_SHIPPING]: noReturnShipping,
    [submissionFields.TRACKING_NUMBER]: tracking,
  };
}

function submissionFieldsToFormikValues(submission) {
  const fields = submission?.fields ?? submission ?? {};

  if (_.isEmpty(fields)) {
    return {};
  }

  return {
    cards: fields[submissionFields.CARDS_SENT],
    date: fields[submissionFields.DATE_SENT],
    noReturnShipping: fields[submissionFields.NO_RETURN_SHIPPING],
    tracking: fields[submissionFields.TRACKING_NUMBER],
    couponCode: fields[submissionFields.COUPON_CODE],
  };
}

export default injectIntl(DraftSubmissionPage);
